import type {CampaignId} from "./campaignPlayback";

export type BadgeShape = "pill" | "gold" | "stamp" | "word" | "blob" | "chip" | "tag" | "layered" | "tab";
const shapes: Record<CampaignId, BadgeShape> = {
	bloom: "gold", eclipse: "gold", contour: "pill",
	crumple: "gold", cartoon: "blob", newspaper: "stamp", cover: "word", ink: "tab",
	showcase: "tab", kinetic: "tag", cutpaper: "layered", collage: "tag",
	pumpkin: "pill", vampire: "pill", zombie: "pill", witch: "pill",
};
export const badgeShapeFor = (id: CampaignId): BadgeShape => shapes[id];
export const badgeMotionFor = (shape: BadgeShape, reduced = false) =>
	!reduced && (shape === "blob" || shape === "tag") ? "spring" : "instant";

export interface BadgePoint {x: number; y: number}
export interface BadgeSpring extends BadgePoint {vx: number; vy: number}

/** The approved prototype's per-frame spring, with a finite rest threshold. */
export function springStep(state: BadgeSpring, target: BadgePoint): BadgeSpring {
	const stiffness = .14, damping = .72, rest = .05;
	const vx = (state.vx + (target.x - state.x) * stiffness) * damping;
	const vy = (state.vy + (target.y - state.y) * stiffness) * damping;
	const x = state.x + vx, y = state.y + vy;
	return Math.hypot(target.x - x, target.y - y) < rest && Math.hypot(vx, vy) < rest
		? {...target, vx: 0, vy: 0} : {x, y, vx, vy};
}
export function directionAngle(dx: number, dy: number): number {
	if (!dx && !dy) return 0;
	// Vertical is zero so horizontal reversals tilt to opposite sides.
	return Math.max(-25, Math.min(25, Math.atan2(dx, -dy) * 180 / Math.PI));
}
/** Hand tremors must not steer the tag or restart the blob's bounce. */
export const TAG_STEER_MIN_PX = 3;
export function steerAngle(current: number, dx: number, dy: number): number {
	if (Math.hypot(dx, dy) < TAG_STEER_MIN_PX) return current;
	const target = directionAngle(dx, dy);
	const next = current + (target - current) * .18;
	return Math.abs(next - target) < .05 ? target : next;
}
export const reversesDirection = (previous: BadgePoint, next: BadgePoint): boolean =>
	previous.x * next.x + previous.y * next.y < 0;

/** Flip at the viewport edge instead of clipping the translated face. */
export function cursorPosition(x: number, y: number, width: number, height: number, badgeWidth: number, badgeHeight: number): BadgePoint {
	const gap = 18;
	return {
		x: Math.max(8, x + gap + badgeWidth > width ? x - badgeWidth - gap : x + gap),
		y: Math.max(8, y + gap + badgeHeight > height ? y - badgeHeight - gap : y + gap),
	};
}
